import { MEETING_TYPES } from "@config/constants";
import { MeetingType } from "@enums/meetings.enum";
import { formatDateByTimezone } from "./date.util";
import { Timezone } from "@app-types/date.type";
import { User, UserForDropdown } from "@app-types/common.type";
import { IMeetingTag } from "@interfaces/tag.interface";

export const getMeetingTypeData = (type: MeetingType | undefined) => {
  return MEETING_TYPES.find((meetingType) => meetingType.type === type);
};

/**
 * Formats meeting time information based on start and end times and user's timezone.
 *
 * @param {Date} start_time - The start time of the meeting.
 * @param {Date} end_time - The end time of the meeting.
 * @param {Timezone} userTimezoneOffset - The user's timezone offset.
 * @returns {Object} An object containing formatted meeting time information:
 *   - parsedMeetingTime: A string representation of the full meeting time range.
 *   - meetingStartDate: The formatted start date of the meeting.
 *   - meetingStartTime: The formatted start time of the meeting (hour and minute).
 *   - meetingStartTimePart: The AM/PM indicator for the start time.
 *   - meetingEndTime: The formatted end time of the meeting (hour and minute).
 *   - meetingEndTimePart: The AM/PM indicator for the end time.
 *
 * @description
 * This function takes the start and end times of a meeting and the user's timezone offset,
 * and returns an object with various formatted time components. It uses the formatDateByTimezone
 * utility to ensure all times are correctly adjusted for the user's timezone.
 *
 * @example
 * const start = new Date('2023-05-15T09:00:00Z');
 * const end = new Date('2023-05-15T10:30:00Z');
 * const userTimezone = '+01:00';
 * const result = getMeetingTime(start, end, userTimezone);
 * console.log(result);
 * // Output:
 * // {
 * //   parsedMeetingTime: 'Monday, May 15 • 10:00-11:30AM',
 * //   meetingStartDate: 'Monday, May 15',
 * //   meetingStartTime: '10:00',
 * //   meetingStartTimePart: 'AM',
 * //   meetingEndTime: '11:30',
 * //   meetingEndTimePart: 'AM'
 * // }
 */
export const getParsedMeetingTime = (
  start_time: Date,
  end_time: Date,
  userTimezoneOffset: Timezone
) => {
  const meetingStartDate = start_time
    ? formatDateByTimezone(start_time, userTimezoneOffset, `dddd, MMMM D`)
    : "";

  const meetingStartTime = start_time
    ? formatDateByTimezone(start_time, userTimezoneOffset, `h:mm`)
    : "";
  const meetingStartTimePart = start_time
    ? formatDateByTimezone(start_time, userTimezoneOffset, `A`)
    : "";

  const meetingEndTime = end_time ? formatDateByTimezone(end_time, userTimezoneOffset, `h:mm`) : "";
  const meetingEndTimePart = end_time
    ? formatDateByTimezone(end_time, userTimezoneOffset, `A`)
    : "";

  const parsedMeetingTime = `${meetingStartDate} • ${meetingStartTime}${
    meetingStartTimePart === meetingEndTimePart ? "" : meetingStartTimePart
  }-${meetingEndTime}${meetingEndTimePart}`;

  return {
    parsedMeetingTime,
    meetingStartDate,
    meetingStartTime,
    meetingStartTimePart,
    meetingEndTime,
    meetingEndTimePart,
  };
};

export const getSelectedOrUnselectedAttendees = (
  attendees: User[],
  usersOptions: UserForDropdown[]
) => {
  if (attendees.length < usersOptions.length) {
    return [...usersOptions];
  } else {
    return [];
  }
};

export function filterMeetings(itemsForGroup: IMeetingTag[]): IMeetingTag[] {
  const now = new Date().toISOString();

  // Cast to any[] to work with the union type
  const itemsArray = itemsForGroup as any[];

  // Type guard to ensure we're working with MeetingTag objects
  const isMeetingTag = (item: any): item is IMeetingTag =>
    item && typeof item.start_time === "string";

  const meetingItems = itemsArray.filter(isMeetingTag);

  const pastMeetings = meetingItems
    .filter((item: IMeetingTag) => item.start_time < now)
    .sort((a: IMeetingTag, b: IMeetingTag) => (b.start_time > a.start_time ? 1 : -1));
  const futureMeetings = meetingItems
    .filter((item: IMeetingTag) => item.start_time >= now)
    .sort((a: IMeetingTag, b: IMeetingTag) => (a.start_time > b.start_time ? 1 : -1));
  let pastCount = 0;
  let futureCount = 0;

  if (pastMeetings.length === 0) {
    // Case 4: Only future meetings - show 5 earliest
    futureCount = Math.min(5, futureMeetings.length);
  } else if (futureMeetings.length === 0) {
    // Case 5: Only past meetings - show 5 most recent
    pastCount = Math.min(5, pastMeetings.length);
  } else {
    // Cases 1, 2, 3: Both past and future meetings
    if (pastMeetings.length <= 3) {
      // Case 2: Few past meetings (≤3), show all past + fill with future
      pastCount = pastMeetings.length;
      futureCount = Math.min(5 - pastCount, futureMeetings.length);
    } else if (futureMeetings.length <= 2) {
      // Case 3: Few future meetings (≤2), show all future + fill with past
      futureCount = futureMeetings.length;
      pastCount = Math.min(5 - futureCount, pastMeetings.length);
    } else {
      // Case 1: Both have sufficient meetings - show 3 past + 2 future
      pastCount = 3;
      futureCount = 2;
    }
  }

  const formattedPastMeetings = [];
  const formattedFutureMeetings = [];
  for (let i = 0; i < pastCount; i++) {
    formattedPastMeetings.push(pastMeetings[i]);
  }
  for (let i = 0; i < futureCount; i++) {
    formattedFutureMeetings.push(futureMeetings[i]);
  }

  const result = [...formattedPastMeetings.reverse(), ...formattedFutureMeetings];
  return result as typeof itemsForGroup;
}
